import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';
import { Input } from 'common/components';
import { useCognito } from 'common/hooks';

import styles from '../ForgotPasswordModal.module.scss';

export type RequestFormData = {
    email: string;
};

type RequestStepProps = {
    form: UseFormReturn<RequestFormData>;
    onSuccess: (email: string, cognitoUser: AmazonCognitoIdentity.CognitoUser) => void;
};

export const RequestStep: React.FC<RequestStepProps> = ({ form, onSuccess }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [generalError, setGeneralError] = useState('');
    const { forgotPassword } = useCognito();

    const handleRequestReset = async (data: RequestFormData) => {
        // Clear any previous general errors
        setGeneralError('');
        setIsLoading(true);

        try {
            const user = forgotPassword(data.email, {
                onSuccess: () => {
                    onSuccess(data.email, user);
                    setIsLoading(false);
                },
                onFailure: (err) => {
                    console.error('Password reset request failed:', err);
                    setGeneralError(err.message || 'Failed to send password reset code');
                    setIsLoading(false);
                },
            });
        } catch (err: any) {
            console.error('Password reset request error:', err);
            setGeneralError(err.message || 'An unexpected error occurred');
            setIsLoading(false);
        }
    };

    return (
        <form id='request-form' onSubmit={form.handleSubmit(handleRequestReset)} noValidate>
            <div className={styles.stepContainer}>
                <div className={styles.description}>
                    Enter your email address and we'll send you a verification code to reset your password.
                </div>

                <Input
                    label='Email Address'
                    type='email'
                    placeholder='Enter your email address'
                    disabled={isLoading}
                    error={!!form.formState.errors.email}
                    errorText={form.formState.errors.email?.message}
                    {...form.register('email', {
                        required: 'Please enter your email address',
                        validate: (value) => {
                            if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
                                return 'Please enter a valid email address';
                            }
                            return true;
                        },
                    })}
                />

                {generalError && <div className={styles.error}>{generalError}</div>}
            </div>
        </form>
    );
};
